import React from 'react';
import { Box, Grid, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import TextField from "../../../../../controls/TextField";
import { useFormContext } from "react-hook-form";

import { handleZodError, modifierValidation, multiplierValidation } from './_scoreValidation';
import { BaseRisk } from '../../../api/api-types';

//TODO: consolidate types (dupe types not necessary) (the type just below appears unused)
//type RiskProps = RiskBasedCredit; 

// Need to figure out why this is a double array
type Props = Partial<Omit<BaseRisk, "dataRobot">>

export type RiskInput = Props

const RiskForm: React.FC<Props> = ({ increment, range, scoreMultiplier, scoreModifier }) => {

    const theme = useTheme();
    const isMD = useMediaQuery(theme.breakpoints.up('md'));
    const { register, errors, getValues, trigger } = useFormContext<RiskInput>();

    return (
        <Grid container direction="column" spacing={2}>
            <Grid item>
                <Grid container alignContent={'flex-start'} justifyContent={'center'} direction={'row'} spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField defaultValue={increment ?? ''}
                            name={`increment`} fullWidth
                            label={'Increment'}
                            onChange={() => {
                                trigger('increment');
                            }}
                            inputProps={{
                                ref: register({
                                    required: 'Required',
                                    validate: (v: string) => {
                                        const val = Number(v);
                                        if (isNaN(val) || v === '' || val <= 0) {
                                            return 'Must be a positive number';
                                        }
                                        return true;
                                    }
                                })
                            }}
                            error={Boolean(errors.increment)}
                            helperText={(errors.increment && errors.increment.message) || ' '} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField defaultValue={range?.low ?? ''}
                            name={`range.low`} fullWidth
                            label={'Range (low)'}
                            onChange={() => {
                                trigger(['range.low', 'range.high']);
                            }}
                            inputProps={{
                                ref: register({
                                    required: 'Required',
                                    validate: (v: string) => {
                                        const val = Number(v);
                                        if (isNaN(val) || v === '') return 'Not a valid number';
                                        const highRaw = getValues('range.high');
                                        if (highRaw !== undefined && highRaw !== '' && !isNaN(Number(highRaw))) {
                                            if (val >= Number(highRaw)) {
                                                return 'Range (low) must be less than Range (high)';
                                            }
                                        }
                                        return true;
                                    }
                                }),
                                onChange: () => {
                                    trigger(['range.low', 'range.high']);
                                }
                            }}
                            error={Boolean(errors.range?.low)}
                            helperText={(errors.range?.low && errors.range.low.message) || ' '} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField defaultValue={range?.high ?? ''}
                            name={`range.high`} fullWidth
                            label={'Range (high)'}
                            onChange={() => {
                                trigger(['range.low', 'range.high']);
                            }}
                            inputProps={{
                                ref: register({
                                    required: 'Required',
                                    validate: (v: string) => {
                                        const val = Number(v);
                                        if (isNaN(val) || v === '') return 'Not a valid number';
                                        const lowRaw = getValues('range.low');
                                        if (lowRaw !== undefined && lowRaw !== '' && !isNaN(Number(lowRaw))) {
                                            if (val <= Number(lowRaw)) {
                                                return 'Range (high) must be greater than Range (low)';
                                            }
                                        }
                                        return true;
                                    }
                                }),
                                onChange: () => {
                                    trigger(['range.low', 'range.high']);
                                }
                            }}
                            error={Boolean(errors.range?.high)}
                            helperText={(errors.range?.high && errors.range.high.message) || ' '} />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <Typography align="center" variant="subtitle1" gutterBottom >
                    <Box sx={{ fontWeight: 500, fontSize: '1.3em' }}>Optimizer Control</Box>
                </Typography>
                <Paper variant="outlined" >
                    <Box sx={{ p: 1 }}>
                        <Box sx={{ margin: 4 }}>
                            <Grid container justifyContent='center' alignContent='center' alignItems="center" spacing={1} >
                                <Grid item xs={6} md="auto" >
                                    <Box display="flex" sx={{ gridGap: 4, alignItems: "center" }}>
                                        {isMD && (<> <Typography variant="h5" gutterBottom>(</Typography>
                                            <Typography variant="h5" gutterBottom><Box fontStyle="italic">{"{score}"}</Box></Typography>
                                            <Typography variant="h5" gutterBottom> -</Typography></>)}
                                        <TextField defaultValue={scoreModifier}
                                            name={"scoreModifier"}
                                            label={'Modifier'}
                                            inputProps={{
                                                ref: register({
                                                    required: 'Required', validate: {
                                                        validate: value => {
                                                            const isValid = modifierValidation.safeParse(value)
                                                            if (isValid.success) {
                                                                return true
                                                            }
                                                            else {
                                                                return handleZodError(isValid)
                                                            }
                                                        }
                                                    }
                                                })
                                            }}
                                            error={Boolean(errors.scoreModifier)}
                                            helperText={(errors.scoreModifier && errors.scoreModifier.message) || ' '} />
                                        {isMD && (<Typography variant="h5" gutterBottom>)</Typography>)}
                                    </Box>
                                </Grid>
                                {isMD && (<Grid item xs={12} md="auto" >
                                    <Typography gutterBottom > X</Typography>
                                </Grid>)}
                                <Grid item xs={6} md="auto">
                                    <Box>
                                        <TextField
                                            fullWidth
                                            defaultValue={scoreMultiplier}
                                            name={"scoreMultiplier"}
                                            label={'Multiplier'}
                                            inputProps={{
                                                ref: register({
                                                    required: 'Required', validate: value => {
                                                        const isValid = multiplierValidation.safeParse(value)
                                                        if (isValid.success) {
                                                            return true
                                                        }
                                                        else {
                                                            return handleZodError(isValid)
                                                        }
                                                    }
                                                })
                                            }}
                                            error={Boolean(errors.scoreMultiplier)}
                                            helperText={(errors.scoreMultiplier && errors.scoreMultiplier.message) || ' '} />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
}

export default RiskForm;